import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { GridOffset, GridWrapper } from "@insite/mobius/GridContainer/GridContainer";
import { GridItemStyle } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    location: getLocation(state),
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    logoLinkSearchContainer?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, true, true, true],
        css: css`
            box-shadow: 0 2px 4px 0 hsla(0, 0%, 41%, 0.32);
            border-bottom: 1px solid #e5e5e5;
            position: sticky !important;
            top: 0;
            z-index: 99;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            background-color: #953636;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            ~ main[class^="PageStyle"] {
                padding: 15px 1.5rem;
                min-height: 600px;
            }
            ~main[data-test-selector^="productDetails_productId"]{
                padding: 0px !important;
                min-width: 100%;
            }
            ~ main[data-test-selector^="homePage"] {
                padding: 0px !important;
                min-width: 100%;
                > div[class^="Wrapper"] {
                    > div[class^="sc"] {
                        min-width: 100%;
                    }
                    ${GridWrapper} {
                        ${GridOffset} {
                            ${GridItemStyle}:first-child {
                                padding: 0px;
                            }
                        }
                    }
                }
            }
        `,
    },
    container: {
        css: css`
            display: flex;
            background-color: #f3f3f3;
            /* padding: 0rem 1.5rem; */
            > div[class^="Wrapper"] {
                > ${GridWrapper} {
                    margin: 0 auto;
                    > ${GridOffset} > ${GridItemStyle} > div[class^="Wrapper"] {
                        padding: 0rem 1.5rem;
                        position: relative;
                        nav[class^="OverflowWrapper"] {
                            position: unset !important;
                        }
                    }
                }
            }
        `,
    },
    logoLinkSearchContainer: {
        css: css`
            display: flex;
            > div[class^="Wrapper"] {
                > ${GridWrapper} {
                    margin: 0 auto;
                    > ${GridOffset} > ${GridItemStyle} > div[class^="Wrapper"] {
                        padding: 0.275rem 1.5rem;
                    }
                }
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const Header = ({ id, siteName, session, toaster, history, myAccountPageUrl, dismissRfqUpdates, location }: Props) => {
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (!(window as any).customRedirectToUrl) {
            (window as any).customRedirectToUrl = (url: string) => {
                history.push(url);
            };
        }
    }, []);

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const googleAdsScriptPagePath = () => {
        const scriptTag = document.createElement("script");
        scriptTag.text = `window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'AW-1051637885', { 'page_path': '${window.location.pathname}' }); `;
        document.head.appendChild(scriptTag);
    };

    const loadGoogleAdsScript = () => {
        const script = document.createElement("script");
        script.src = "https://www.googletagmanager.com/gtag/js?id=AW-1051637885";
        script.async = true;
        document.head.appendChild(script);
    };

    useEffect(() => {
        (window as any).Northbeam?.trackPageView();
        loadGoogleAdsScript();
        googleAdsScriptPagePath();
    }, [location]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            <StyledWrapper role="banner" {...styles.page}>
                <Zone contentId={id} zoneName="SecondaryNavigation" />
            </StyledWrapper>
            <Page as="header" isHeader={true} {...styles.page}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    {/* <Zone contentId={id} zoneName="SecondaryNavigation" /> */}
                    <StyledWrapper {...styles.logoLinkSearchContainer}>
                        <Zone contentId={id} zoneName="LogoLinkSearchContainer" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.container}>
                        <Zone contentId={id} zoneName="MainNavigationContainer" />
                    </StyledWrapper>
                    <Zone contentId={id} zoneName="Breadcrumb" />
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
